import { Trans } from "@lingui/react/macro";
import { Navigate, useLocation } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { ApiError, useApiErrorMessage } from "@/shared/api";
import { Button } from "@/shared/ui/button";
import { Skeleton } from "@/shared/ui/skeleton";

import { useCurrentUser } from "../api/useCurrentUser";
import { useRecordLanding } from "../api/useRecordLanding";
import { LOGIN_PAGE } from "../lib/leavePage";
import { useProfileLocale } from "../lib/useProfileLocale";

type RequireAuthProps = {
  children: ReactNode;
};

// Показывает страницу только вошедшему пользователю. Без сессии — на `/login?next=<эта страница>`.
// Обычно 401 раньше ловит middleware клиента API (полный переход с перезагрузкой),
// здесь — та же реакция средствами роутера, если обработчик 401 заменён.
// После входа язык интерфейса берётся из профиля, а новый игрок отправляет, откуда пришёл.
export const RequireAuth = ({ children }: RequireAuthProps) => {
  const { data: user, error, refetch, isRefetching } = useCurrentUser();
  const { href, pathname } = useLocation();
  const errorMessage = useApiErrorMessage(error);
  useProfileLocale(user?.locale);
  useRecordLanding(user);

  if (user) {
    return children;
  }
  if (error instanceof ApiError && error.status === 401) {
    // Адрес уже сменился на вход, а оболочка ещё не перерисовалась: второй переход
    // потерял бы `next`.
    if (pathname === LOGIN_PAGE) {
      return null;
    }
    return <Navigate to="/login" search={{ next: href }} replace />;
  }
  if (error) {
    return (
      <main
        role="alert"
        className="mx-auto flex max-w-[1200px] flex-col items-start gap-3 px-6 py-16"
      >
        <p className="text-foreground-soft">{errorMessage}</p>
        <Button
          variant="outline"
          loading={isRefetching}
          onClick={() => {
            void refetch();
          }}
        >
          <Trans>Try again</Trans>
        </Button>
      </main>
    );
  }
  return (
    <div aria-busy="true" className="mx-auto flex max-w-[1200px] flex-col gap-4 px-6 py-6">
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
};
